import { FocusEvent, useCallback, useRef, useState } from 'react';
import { Navigate, createFileRoute, useRouter } from '@tanstack/react-router';
import { Button, InputFooter, Spinner, cn } from '@bond/bricks';
import { useToast } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { motion } from 'motion/react';
import logo from '@/assets/logo.png';
import more from '@/assets/more.svg';
import defaultProfilePic from '@/assets/profileAnonPicInverse.png';
import { QuestionAndAnswer } from '@/components/Profile/QuestionAndAnswer';
import { SuggestedQuestions } from '@/components/Profile/SuggestedQuestions';
import ProfileConnectionButton from '@/components/ProfileConnectionButton';
import BottomSheet from '@/components/lib/BottomSheet';
import { useConnectionMutations } from '@/hooks/useConnectionMutations';
import { useStreamingAnswer } from '@/hooks/useStreamingAnswer';
import { useIsKeyboardOpen, useVisualViewportSize } from '@/hooks/useViewPortSize';
import { useAppSelector } from '@/store/hooks';
import { getStatsigClient } from '@/utils/statsig';
import { GetQuestionSuggestionsResponse } from '../../../../shared/types/questionSuggestionTypes';
import { UserLookupResponse, UserRelationship } from '../../../../shared/types/userTypes';

export const Route = createFileRoute('/profile/$uniqId')({
  component: ProfilePage,
  loader: async ({ params }) => {
    const { uniqId } = params;
    getStatsigClient().logEvent('start_fetching_asklink_data');
    const userRes = await axios.get<UserLookupResponse>('/api/user/lookup', {
      params: {
        uniqId,
      },
    });
    getStatsigClient().logEvent('end_fetching_asklink_data');
    return userRes.data.data;
  },
  errorComponent: () => <Navigate to="/" />,
});

function ProfilePage() {
  const receiver = Route.useLoaderData();
  const authState = useAppSelector((state) => state.auth);
  const [inputValue, setInputValue] = useState('');
  const viewportSize = useVisualViewportSize();
  const isKeyboardOpen = useIsKeyboardOpen();
  const viewportHeight = viewportSize ? viewportSize[1] : window.innerHeight;

  const [isMoreSheetOpen, setIsMoreSheetOpen] = useState(false);
  const [isDisconnectConfirmSheetOpen, setIsDisconnectConfirmSheetOpen] = useState(false);
  const { disconnect, isLoading } = useConnectionMutations();
  const router = useRouter();
  const [shakingButtonAnimationX, setShakingButtonAnimationX] = useState<number | number[]>(0);
  const toast = useToast();
  const [submittedQuestion, setSubmittedQuestion] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { answer, isStreaming, streamAnswer, stopStreaming } = useStreamingAnswer();
  const askerUserId = authState.userId;

  const { data: questionSuggestions } = useQuery({
    queryKey: ['questionSuggestions', receiver.id],
    queryFn: async () => {
      const response = await axios.get<GetQuestionSuggestionsResponse>(
        '/api/questionSuggestions/getQuestionSuggestions',
        {
          params: {
            userId: receiver.id,
          },
        },
      );
      if (response.data.success) {
        return response.data.data.suggestions;
      }

      // We don't need to handle errors as it's suggested questions
      console.error('Error fetching question suggestions', response.data.message);
      return [];
    },
    enabled: !!receiver.id,
  });

  const handleQuestionBoxFocus = useCallback(
    (e: FocusEvent<HTMLTextAreaElement>) => {
      getStatsigClient()?.logEvent('tap_question_box');
      if (!authState.isAuthenticated) {
        e.target.blur();
        router.navigate({ to: '/signup/phone-signup' });
        return;
      }
      if (receiver.userRelationship !== UserRelationship.CONNECTED) {
        e.target.blur();
        toast({
          title: 'Connect to ask a question',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
        setShakingButtonAnimationX([-20, 20, -20, 20, 0]);
        setTimeout(() => {
          setShakingButtonAnimationX(0);
        }, 500);
      }
    },
    [receiver.userRelationship, authState.isAuthenticated, toast, router],
  );

  const handleSuggestionClick = useCallback((question: string) => {
    setInputValue(question);
    inputRef.current?.focus();
  }, []);

  return (
    <div
      className={cn('flex flex-col w-full overflow-hidden bg-background', 'mx-auto max-w-5xl')}
      style={{
        height: '100dvh',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {/* Fixed Header */}
      <div
        className={cn(
          'py-4 px-9 flex-shrink-0 relative',
          'grid grid-rows-2 grid-cols-[minmax(70px,1fr)_auto_minmax(70px,1fr)]',
        )}
      >
        <img
          src={logo}
          alt="Bond Logo"
          className={cn('h-7 object-contain', 'row-start-1 col-start-1')}
          onClick={() => router.navigate({ to: '/' })}
        />
        {submittedQuestion && (
          <div className={cn('flex flex-col items-center justify-center gap-3', 'col-start-2 row-span-2')}>
            <img src={receiver.profilePic ?? defaultProfilePic} alt="Profile" className="size-10 rounded-full" />
            <h1 className="text-header-3 text-text-primary">{receiver.displayName}</h1>
          </div>
        )}
        {!authState.isAuthenticated && (
          <div className="row-start-1 col-start-3 justify-self-end">
            <Button onClick={() => router.navigate({ to: '/signup/phone-signup' })}>Signup</Button>
          </div>
        )}
        {receiver?.userRelationship === UserRelationship.CONNECTED && authState.userId !== receiver.id && (
          <div
            className="cursor-pointer row-start-1 col-start-3 justify-self-end pt-[6px]"
            onClick={() => setIsMoreSheetOpen(true)}
          >
            <img src={more} />
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div
        className={cn('flex-1 px-4', 'flex flex-col items-center relative px-4 py-2', 'overflow-hidden')}
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {!submittedQuestion && (
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-3',
              'absolute left-1/2 top-[30dvh] -translate-x-1/2 -translate-y-1/2 z-10',
            )}
          >
            <img src={receiver.profilePic ?? defaultProfilePic} alt="Profile" className="size-18 rounded-full" />
            <h1 className="text-header-3 text-text-primary">{receiver.displayName}</h1>
            <p className="text-body-small text-text-secondary">{receiver.totalConnectionsCount} connections</p>
            {authState.isAuthenticated && receiver.id !== authState.userId && (
              <motion.div
                className="flex flex-row items-center justify-center mb-7 mt-3"
                animate={{ x: shakingButtonAnimationX }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              >
                <ProfileConnectionButton
                  associatedUserId={receiver.id}
                  relationship={receiver.userRelationship ?? UserRelationship.DISCONNECTED}
                  onRelationshipChange={() => {
                    router.invalidate();
                  }}
                />
              </motion.div>
            )}
          </div>
        )}
        {submittedQuestion && (
          <QuestionAndAnswer question={submittedQuestion} answer={answer} isStreaming={isStreaming} />
        )}
      </div>

      {/* Fixed Footer with Input */}
      <div
        className="flex-shrink-0 mb-4"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          transform:
            isKeyboardOpen && viewportSize ? `translateY(${window.innerHeight - viewportHeight}px)` : 'translateY(0)',
          transition: 'transform 0.3s ease-out',
        }}
      >
        {!submittedQuestion && (
          <SuggestedQuestions suggestions={questionSuggestions} onQuestionClick={handleSuggestionClick} />
        )}
        <div className="mx-4">
          <InputFooter
            ref={inputRef}
            profilePic={authState.profilePic ?? defaultProfilePic}
            hideProfilePic={!authState.isAuthenticated}
            value={inputValue}
            onChange={setInputValue}
            isSubmitting={isStreaming}
            onStop={() => {
              stopStreaming();
              setSubmittedQuestion(null);
              getStatsigClient().logEvent('stop_question_generation');
            }}
            onSubmit={async () => {
              if (!inputValue.trim()) return;

              const query = inputValue.trim();
              setSubmittedQuestion(query);
              setInputValue('');

              // Log events based on user context
              if (receiver.id === askerUserId) {
                getStatsigClient().logEvent('submit_question_to_own_profile', query);
              } else {
                getStatsigClient().logEvent('submit_question_to_external_profile', query);
              }

              // Stream the answer
              streamAnswer(
                {
                  query,
                  receiverUserId: receiver.id,
                  askerUserId: askerUserId!,
                },
                {
                  onSuccess: (result) => {},
                  onError: (error) => {
                    if (error.name !== 'AbortError') {
                      toast({
                        title: 'Error',
                        description: 'Failed to get answer. Please try again.',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                        position: 'top',
                      });
                    }
                  },
                },
              );
            }}
            onFocus={handleQuestionBoxFocus}
          />
        </div>
      </div>
      <BottomSheet isOpen={isMoreSheetOpen} onClose={() => setIsMoreSheetOpen(false)}>
        <div>
          <div className="flex flex-col items-center text-center">
            <div
              className="text-red-400 font-medium py-4 w-full text-center"
              onClick={() => {
                setIsMoreSheetOpen(false);
                setTimeout(() => {
                  setIsDisconnectConfirmSheetOpen(true);
                }, 100);
              }}
            >
              Disconnect
            </div>
            <div
              className="font-medium py-4 w-full text-center"
              // Chakra UI resets the border-top to 0px, so we need to add it back
              // Tailwind won't work here because of it
              style={{ borderTop: '1px solid oklch(0.928 0.006 264.531)' }}
              onClick={() => setIsMoreSheetOpen(false)}
            >
              Cancel
            </div>
          </div>
        </div>
      </BottomSheet>
      <BottomSheet isOpen={isDisconnectConfirmSheetOpen} onClose={() => setIsDisconnectConfirmSheetOpen(false)}>
        <div className="flex flex-col items-center text-center">
          <div className="text-lg mt-2 p-5">Do you want to disconnect with {receiver.displayName}?</div>
          <div
            className="text-red-400 font-medium py-4 w-full text-center"
            // Chakra UI resets the border-top to 0px, so we need to add it back
            // Tailwind won't work here because of it
            style={{ borderTop: '1px solid oklch(0.928 0.006 264.531)' }}
            onClick={() =>
              disconnect(receiver.id, {
                onSuccess: () => {
                  router.invalidate({ sync: true });
                  setIsDisconnectConfirmSheetOpen(false);
                },
              })
            }
          >
            {isLoading.disconnect ? <Spinner /> : 'Confirm'}
          </div>
          <div
            className="font-medium py-4 w-full text-center"
            // Chakra UI resets the border-top to 0px, so we need to add it back
            // Tailwind won't work here because of it
            style={{ borderTop: '1px solid oklch(0.928 0.006 264.531)' }}
            onClick={() => setIsDisconnectConfirmSheetOpen(false)}
          >
            Cancel
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}
